<template>
    <div>
        <div class="app-title">
            <em-view-title ref="emViewTitle" v-bind:crudState="$_emWorkflowTaskMixin_pathType"></em-view-title>
        </div>
        <section class="app-content">
            <div id="entity-viewer" class="content" v-menu-permission="'list'">
                <div class="card">
                    <div id="collapseOne" class="collapse show" aria-labelledby="headingOne"
                         data-parent="#accordionFilter">
                        <div class="card-body">
                            <em-workflow-business-data-editor :entity="modelName"
                                                              :allow-edit="false"
                                                              :business-data="$_emWorkflowTaskMixin_businessData">
                            </em-workflow-business-data-editor>
                        </div>
                    </div>
                </div>
            </div>

            <!--
                URS 需求_new R51–R64：处置的「待办」页要展示每个资产的固资号 / 子固资号 / 资产名称 /
                设备编码 / 成本中心 / 部门 / 使用位置 / 原值 / 已提折旧 / 账面价值 / 资本化日期 /
                折旧年限 / 折旧年限-月 / 已使用年限。原值等财务字段在处置流程里对所有审批人可见
                （「仅财务可见」只约束档案模块，plan 已知约束）。
                明细走 GET /detail/{id} 单独查一次，不进列表查询（一条处置单可能带几十个资产）。
            -->
            <div class="content">
                <div class="card my-1">
                    <div class="card-body">
                        <h5>
                            <span class="text-primary">
                                {{ $t('label.em.asset.emArchFixedAssetDisposal.itemBlock') }}
                            </span>
                            <span class="ml-2 text-muted">{{ itemRows.length }}</span>
                        </h5>
                        <b-alert :show="showOpinionResetTip" variant="warning" class="py-2">
                            {{ $t('label.em.asset.emArchFixedAssetDisposal.opinionResetTip') }}
                        </b-alert>
                        <div class="fad-items">
                            <mini-table :key="itemTableKey"
                                        header-title=""
                                        disable-header
                                        read-only
                                        row-key="id"
                                        :fields="itemFields"
                                        :items="itemRows"
                                        :loading="itemLoading">
                            </mini-table>
                        </div>
                    </div>
                </div>
            </div>

            <!--
                附件（测试反馈「处置 3」/「处置 6」）：流程每个节点、以及只读查看详情时都能下载。
                  发起人附件 —— 主表 attachments（发起时必填）；
                  出门证附件 —— 主表 gate_pass_attachments，task_owner_gate_pass 节点可选上传，
                               销账节点及之后查看详情的人都能下载；没传过就不显示这一块。
                两批附件挂接在不同字段上，互不覆盖。只读展示（不传 is-editing），点文件名即下载。
                attachment-ids 随 businessData 异步补回来，组件内部 watch 会自己重新拉列表。
            -->
            <div class="content">
                <div class="card my-1">
                    <div class="card-body">
                        <h5>
                            <i class="fa fa-fw fa-paperclip text-primary"></i>
                            <span class="text-primary">
                                {{ $t('label.em.asset.emArchFixedAssetDisposal.attachmentBlock') }}
                            </span>
                        </h5>
                        <em-attachment v-if="applicantAttachments"
                                       get-items-by-self
                                       :attachment-ids="applicantAttachments"></em-attachment>
                        <div v-else class="text-muted small mb-2">
                            {{ $t('label.em.asset.emArchFixedAssetDisposal.noAttachment') }}
                        </div>

                        <template v-if="gatePassAttachments">
                            <h5 class="mt-3">
                                <i class="fa fa-fw fa-paperclip text-primary"></i>
                                <span class="text-primary">
                                    {{ $t('label.em.asset.emArchFixedAssetDisposal.gatePassAttachmentBlock') }}
                                </span>
                            </h5>
                            <em-attachment get-items-by-self
                                           :attachment-ids="gatePassAttachments"></em-attachment>
                        </template>
                    </div>
                </div>
            </div>

            <WorkflowProcessLine :processDefinitionKey="$_emWorkflowTaskMixin_processDefinitionKey"
                                 :config="config"
                                 ref="processExecution"
                                 @gotItems="setLocalVariables"
                                 :businessKey="$_emWorkflowTaskMixin_businessKey">
            </WorkflowProcessLine>
            <div id="em-workflow-task-complete" class="content" v-if="$_emWorkflowTaskMixin_isShowTaskComplete">
                <!--
                    三个 prop 缺一不可，缺了都是「不报错但流程走歪」（S6 已知约束）：
                      complete-api      → 打业务侧 /emArchFixedAssetDisposal/completeTask，
                                          由 service 先把本节点录入的处置类型 / 意见 / 出门证编号 /
                                          被通知人落库，再刷新 disposalType / disposalOpinion 两个
                                          网关变量，最后解析下一节点候选人；
                                          不传就直接打工作流服务的 /completeTask，下一节点没有候选人。
                      need-pre-values   → 才会往 taskForm 里塞 fromTask / nextTask / taskUserKey /
                                          processInstanceBusinessKey；applyTaskInput 按 fromTask 分支、
                                          queryUsersByTaskStatus 按 nextTask 分支，全靠这四个。
                                          不传 → findByBusinessKey(null) 返回 null，全不写且不报错。
                      callback-api      → 业务侧 /emArchFixedAssetDisposal/complete-feedback（store 里配的），
                                          负责当前处理人 / 三个 EndEvent 的资产状态回写 / 销账时间 / 通知；
                                          指到基类的 /workflow/complete-feedback 只会更新状态。
                -->
                <WorkflowTaskComplete ref="processComplete"
                                      need-pre-values
                                      :complete-api="completeApi"
                                      :callback-api="$_emWorkflowTaskMixin_callbackApi"
                                      :task-id="$_emWorkflowTaskMixin_taskId"
                                      :businessKey="$_emWorkflowTaskMixin_businessKey"
                                      :status="$_emWorkflowTaskMixin_businessData.status"
                                      :config="config">
                </WorkflowTaskComplete>
            </div>
        </section>
    </div>
</template>

<script>
// 固定资产处置 | 审批页（验收 #11 / #12 UI）
// Author Liang Gangqiang
// Date 2026-09-07

import emWorkflowTaskMixin from "@/mixin/em/workflow/task";
import workflowTaskMixins from "@/components/workflow/mixin/WorkflowTaskMixins";
import WorkflowProcessLine from "@/components/workflow/stuffs/WorkflowProcessLine";
import WorkflowTaskComplete from "@/components/workflow/stuffs/WorkflowTaskComplete";
import MiniTable from "@/components/common/mini-table/MiniTable.vue";
import EmAttachment from "@/components/em/EmAttachment";
import emCommonApiMixin from "@/mixin/em/common/api";
import {costCenterText} from "@/views/em/asset/costCenterText";
import {
    DISPOSAL_OPINION_OPTIONS,
    DISPOSAL_TYPE_OPTIONS,
    OPINION_ALLOWED_BY_TYPE
} from "@/model/em/asset/EmArchFixedAssetDisposalModel";

/**
 * 每个 config 的 key 必须与 BPMN em_arch_fixed_asset_disposal 里的 task id / endEvent id
 * 一字不差（EmArchFixedAssetDisposalStatus 是同一份字面量的 Java 侧枚举）：
 *   task_disposal_init / task_dept_manager_confirm / task_finance_type / task_purchase_opinion /
 *   task_finance_manager_confirm / task_asset_manager_confirm / task_cm_confirm / task_gm_confirm /
 *   task_logistics_gate_pass / task_owner_gate_pass / task_finance_write_off /
 *   Event_end_done / Event_end_hold / Event_end_cancel
 *
 * approved 是 Integer（WorkflowCompleteTaskForm.approved 是 @NotNull Integer），
 * 与 BPMN 的出边条件 ${approved == 1} / ${approved == 2} 一一对应：
 *   1 = 提交 / 同意 / 是      2 = 结束流程 / 不同意 / 否
 *
 * 两个网关按<b>业务变量</b>而不是 approved 判断出边，所以这两处的 nextTask 必须现算：
 *   Gateway_disposal_type    ← disposalType：HOLD → Event_end_hold，SCRAP/SELL → task_purchase_opinion
 *   Gateway_disposal_opinion ← disposalOpinion：SCRAP → task_finance_write_off，
 *                              WASTE_HANDLING / EVALUATED_SALE → task_owner_gate_pass
 *                              （废料处理原先走 task_logistics_gate_pass，已改版；该节点的 config 仍保留，
 *                              给旧版流程定义上停在那里的在途实例用）
 * 不要用 setApproveBeforeSubmit 把 approved 改成 11/12 之类去凑 —— 别的网关的出边条件只认 1/2。
 */
export default {
    name: "emArchFixedAssetDisposalWorkflowTaskView",
    components: {WorkflowProcessLine, WorkflowTaskComplete, MiniTable, EmAttachment},
    mixins: [emWorkflowTaskMixin, workflowTaskMixins, emCommonApiMixin],
    data() {
        return {
            items: [],
            itemLoading: false,
            itemTableKey: 0,
            costCenters: [],
        }
    },
    computed: {
        modelName() {
            return this.$_emRouterHelperMixin_entityName
        },

        completeApi() {
            return process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAssetDisposal/completeTask';
        },

        /**
         * 当前单据的处置类型 / 处置意见（库里的最新值）。
         * 退回后财务 / 采购可能改选，所以每次进本页都要重新读，绝不能记在 data 里。
         * 后端 default-property-inclusion = NON_NULL，没值时是整个 key 消失，判空要容忍 undefined。
         */
        currentDisposalType() {
            let data = this.$_emWorkflowTaskMixin_businessData;
            return data && data.disposalType ? data.disposalType : null;
        },
        currentDisposalOpinion() {
            let data = this.$_emWorkflowTaskMixin_businessData;
            return data && data.disposalOpinion ? data.disposalOpinion : null;
        },
        currentStatus() {
            let data = this.$_emWorkflowTaskMixin_businessData;
            return data && data.status ? data.status : null;
        },

        /**
         * 发起人附件 / 出门证附件的 id 串。后端 NON_NULL，没值时整个 key 消失，统一折成空串
         */
        applicantAttachments() {
            let data = this.$_emWorkflowTaskMixin_businessData;
            return data && data.attachments ? data.attachments : '';
        },
        gatePassAttachments() {
            let data = this.$_emWorkflowTaskMixin_businessData;
            return data && data.gatePassAttachments ? data.gatePassAttachments : '';
        },

        /**
         * 财务重选处置类型会让库里的旧处置意见作废（S8 的 applyTaskInput 会置 null），
         * 页面提前把这件事说清楚，免得用户以为采购上一轮选的意见还在。
         */
        showOpinionResetTip() {
            return this.currentStatus === 'task_finance_type' && !!this.currentDisposalOpinion;
        },

        /**
         * task_gm_confirm「同意」之后的落点 = BPMN Gateway_disposal_opinion 的三条出边。
         * 处置意见此刻已经由采购节点写进库了（HOLD 根本走不到 GM），直接读业务数据即可。
         * 前端必须自己算出来，否则 taskUserKey 会指到错的节点，尾段就没有处理人。
         * 废料处理与评估售卖都进 task_owner_gate_pass（资产负责人 = 申请人）。
         * 旧版流程定义上尚未过 GM 的废料处理实例仍会进 task_logistics_gate_pass，
         * 那一版需要的候选人变量由后端 completeTask 补写，不依赖这里算出的 nextTask。
         */
        nextTaskAfterGm() {
            switch (this.currentDisposalOpinion) {
                case 'WASTE_HANDLING':
                case 'EVALUATED_SALE':
                    return 'task_owner_gate_pass';
                case 'SCRAP':
                default:
                    // 意见缺失属于数据异常（采购节点是必经之路），落到报废尾段最保守：
                    // 财务销账节点无论走哪条尾段都要经过
                    return 'task_finance_write_off';
            }
        },

        /**
         * 采购节点的可选处置意见按处置类型限制（URS R71）：
         *   报废 SCRAP → 只能「报废」
         *   出售 SELL  → 「废料处理」/「评估售卖」
         * 事实表与后端 EmArchFixedAssetDisposalOpinionEnum.allowedBy 同源；
         * 真正的拦截仍在后端 resolveDisposalOpinion（7132），这里只是不给用户选非法项。
         * 类型缺失（数据异常）时给全量选项，让后端把错误报出来而不是页面无声卡死。
         */
        disposalOpinionOptions() {
            let allowed = OPINION_ALLOWED_BY_TYPE[this.currentDisposalType];
            if (!allowed || allowed.length === 0) {
                return DISPOSAL_OPINION_OPTIONS.slice();
            }
            return DISPOSAL_OPINION_OPTIONS.filter(o => allowed.indexOf(o.value) >= 0);
        },

        itemFields() {
            let prefix = 'label.em.asset.emArchFixedAssetDisposal.entity.';
            return [
                {key: 'assetNo', label: prefix + 'assetNo'},
                {key: 'subAssetNo', label: prefix + 'subAssetNo'},
                {key: 'assetName', label: prefix + 'assetName'},
                {key: 'equipmentCode', label: prefix + 'equipmentCode'},
                {key: 'costCenterText', label: prefix + 'costCenterName'},
                {key: 'deptName', label: prefix + 'deptName'},
                {key: 'locationName', label: prefix + 'locationName'},
                {key: 'originalValue', label: prefix + 'originalValue'},
                {key: 'accumulatedDepreciation', label: prefix + 'accumulatedDepreciation'},
                {key: 'netBookValue', label: prefix + 'netBookValue'},
                {key: 'capitalizationDate', label: prefix + 'capitalizationDate'},
                {key: 'depreciationYears', label: prefix + 'depreciationYears'},
                {key: 'depreciationMonths', label: prefix + 'depreciationMonths'},
                {key: 'usedYears', label: prefix + 'usedYears'},
            ];
        },

        /**
         * 明细表只存了 costCenterId / deptId，没有名称快照，
         * 所以用 GET /emArchFixedAsset/costCenters（含所属部门）在前端补名字。
         */
        costCenterMap() {
            let map = {};
            for (let item of this.costCenters) {
                map[item.id] = item;
            }
            return map;
        },

        itemRows() {
            return this.items.map(item => {
                let cc = this.costCenterMap[item.costCenterId];
                return Object.assign({}, item, {
                    costCenterText: cc ? costCenterText(cc.code, cc.name) : '',
                    deptName: cc ? cc.deptName : ''
                });
            });
        },

        config() {
            return {
                // 兜底：下一节点没单独声明时按 <nextTaskId>_candidateUsers 组 taskUserKey。
                // 缺了这一行 getNextTaskUser() 会返回 null，getDefaultTaskUser() 直接抛 TypeError。
                defaultCandidateUser: true,

                // ① 申请人：提交 / 结束流程（Gateway_init）
                'task_disposal_init': {
                    name: '发起处置申请',
                    variables: [
                        {
                            label: '流程操作',
                            name: 'initAction',
                            type: this.VALUE_TYPE.SELECT,
                            saveTo: this.SAVE_TO.APPROVE,
                            options: [
                                {value: 1, text: '提交'},
                                {value: 2, text: '结束流程'}
                            ]
                        }
                    ],
                    withComment: true,
                    approveToNextTask: {
                        1: 'task_dept_manager_confirm',
                        2: 'Event_end_cancel'
                    },
                    // 本节点是 assignee 型（BPMN 里 activiti:assignee="#{task_disposal_init_assignee}"）。
                    // 5 个「不同意」节点退回到这里时，taskUserKey 要拼成 task_disposal_init_assignee
                    // 而不是 _candidateUsers。
                    defaultAssignee: true
                },

                // ② 申请人部门经理：同意 / 不同意（Gateway_dept_manager），备注必填 R70
                'task_dept_manager_confirm': this.approveNode('申请人部门经理审批', 'task_finance_type'),

                // ③ 财务人员：选择处置类型（URS R69 / R70）
                //    Gateway_disposal_type 只看 disposalType，不看 approved；
                //    但 approved 仍是 @NotNull，所以用 forceApprove + setApproveByVariable
                //    把「是否暂不处置」折成 1/2 两个分支，approveToNextTask 就能原生工作，
                //    不必去猜表单里的实时选中值。
                'task_finance_type': {
                    name: '财务人员选择处置类型',
                    variables: [
                        {
                            label: '处置类型',
                            // 变量名就是 BPMN Gateway_disposal_type 的判断变量，
                            // 同时被 S8 的 applyTaskInput 读走落库（不能用 saveTo: DATABASE：
                            // 那条路走通用 PUT /{entity}/selective，SaveForm 没这个字段且会
                            // 重跑一遍 beforeSaveTrigger 的全量校验，S6 已知约束）
                            name: 'disposalType',
                            type: this.VALUE_TYPE.SELECT,
                            require: true,
                            options: DISPOSAL_TYPE_OPTIONS.map(o => ({value: o.value, text: o.text}))
                        }
                    ],
                    withCommentNotNull: true,
                    forceApprove: true,
                    setApproveByVariable: [
                        {
                            variableName: 'disposalType',
                            handler: (approve, value) => value === 'HOLD' ? 2 : 1
                        }
                    ],
                    approveToNextTask: {
                        1: 'task_purchase_opinion',
                        2: 'Event_end_hold'
                    }
                },

                // ④ 采购人员：选择处置意见（URS R71 / R72），只有一条出边
                'task_purchase_opinion': {
                    name: '采购人员选择处置意见',
                    variables: [
                        {
                            label: '处置意见',
                            // BPMN Gateway_disposal_opinion 的判断变量，同样由 S8 的
                            // applyTaskInput 读走落库并校验「类型 × 意见」组合（7132）
                            name: 'disposalOpinion',
                            type: this.VALUE_TYPE.SELECT,
                            require: true,
                            options: this.disposalOpinionOptions.map(o => ({value: o.value, text: o.text}))
                        }
                    ],
                    withCommentNotNull: true,
                    // 本节点没有分支（Flow_purchase_to_finance_manager 是无条件边），
                    // 但 approved 是 @NotNull，固定成 1
                    forceApprove: true,
                    setApproveBeforeSubmit: () => 1,
                    approveToNextTask: {
                        1: 'task_finance_manager_confirm'
                    }
                },

                // ⑤⑥⑦ 财务经理 / 资产经理 / CM：同意 / 不同意，备注必填
                'task_finance_manager_confirm': this.approveNode('财务经理审批', 'task_asset_manager_confirm'),
                'task_asset_manager_confirm': this.approveNode('资产经理审批', 'task_cm_confirm'),
                'task_cm_confirm': this.approveNode('CM管理层审批', 'task_gm_confirm'),

                // ⑧ GM：同意后按处置意见分流到三条尾段（Gateway_gm → Gateway_disposal_opinion）
                'task_gm_confirm': this.approveNode('GM管理层审批', this.nextTaskAfterGm),

                // ⑨ 物流处置员出门证（旧版流程定义的废料处理尾段）：新版 BPMN 已无入边，
                //    保留给改版前启动、停在这里的在途实例走完。D7 不硬关联出门证流程，只记编号
                'task_logistics_gate_pass': this.gatePassNode('物流处置员审批出门证'),

                // ⑩ 资产负责人出门证（评估售卖 / 废料处理两条尾段共用）：assignee 型
                'task_owner_gate_pass': Object.assign(
                        this.gatePassNode('资产负责人审批出门证'),
                        {
                            // BPMN 里 activiti:assignee="#{task_owner_gate_pass_assignee}"，
                            // GM 同意且意见为「评估售卖 / 废料处理」时 taskUserKey 要拼 _assignee
                            defaultAssignee: true
                        }),

                // ⑪ 财务销账：资产状态 = 已销账（URS R81 / R85 / R89）+ 被通知人多选 + 备注
                'task_finance_write_off': {
                    name: '财务人员销账',
                    variables: [
                        {
                            label: '资产状态',
                            name: 'writeOffConfirmed',
                            type: this.VALUE_TYPE.SELECT,
                            saveTo: this.SAVE_TO.APPROVE,
                            // URS 只给了「已销账」一个选项；真正的回写发生在 Event_end_done 的
                            // complete-feedback 里（资产 = WRITTEN_OFF + write_off_time）
                            options: [
                                {value: 1, text: '已销账'}
                            ]
                        }
                    ],
                    withCommentNotNull: true,
                    /*
                     * 被通知人（URS R81 / R85 / R89：可多选，销账后自动发邮件）。
                     * userSelectMap 的非 assignee 项会把选中用户的 loginName 逗号串写进
                     * taskForm.variables[name]，正好是 S8 的 applyTaskInput 读的 notifyUsers；
                     * 不加 saveToDatabase —— 那会触发 PUT /{entity}/selective 的全量校验。
                     */
                    userSelectMap: {
                        '*': [
                            {
                                name: 'notifyUsers',
                                label: '被通知人',
                                labelWithout: '选择被通知人',
                                allowMulti: true,
                                require: false
                            }
                        ]
                    },
                    approveToNextTask: {
                        1: 'Event_end_done'
                    }
                },

                'Event_end_done': {
                    name: '处置完成',
                    isOver: true
                },
                'Event_end_hold': {
                    name: '暂不处置结束',
                    isOver: true
                },
                'Event_end_cancel': {
                    name: '流程结束',
                    isOver: true
                }
            }
        }
    },
    watch: {
        /**
         * 直接刷新本页时 businessData 是异步补回来的（mixin 的 beforeMount 走
         * workflow/find-by-query-form），所以明细不能只在 mounted 里拉一次。
         */
        '$_emWorkflowTaskMixin_businessKey': {
            handler(id) {
                if (id) {
                    this.loadItems();
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.loadCostCenters();
    },
    methods: {
        /**
         * 5 个「同意 / 不同意」节点的通用 config（URS R68 / R73 / R75 / R77 / R79）：
         * approved 1 → 下一节点；2 → 退回申请人 task_disposal_init（BPMN 的 5 条回退线）。
         * 备注必填走 withCommentNotNull。
         */
        approveNode(name, nextTaskWhenAgree) {
            return {
                name: name,
                variables: [
                    {
                        label: '审核结果',
                        name: 'approveResult',
                        type: this.VALUE_TYPE.SELECT,
                        saveTo: this.SAVE_TO.APPROVE,
                        options: [
                            {value: 1, text: '同意'},
                            {value: 2, text: '不同意'}
                        ]
                    }
                ],
                withCommentNotNull: true,
                approveToNextTask: {
                    1: nextTaskWhenAgree,
                    2: 'task_disposal_init'
                }
            };
        },

        /**
         * 两个出门证节点（URS R83 / R84 / R87 / R88）：确认 + 可选出门证编号 + 备注必填。
         * spec D7：出门证不与物流库房的出门证流程硬关联，这里只记编号。
         * 两条边都是无条件边，直接进财务销账。
         * <p>
         * 附件（测试反馈「处置 6」）用 WorkflowTaskComplete 自带的 withAttachment（不强制，不用 NotNull 版）：
         * 上传后的 id 串放在 taskForm.attachmentIds，随 completeApi 一起交给业务侧 completeTask，
         * 由后端写进 gate_pass_attachments 并挂接，页面上方「出门证附件」区展示；
         * 不用 attachmentSaveTo —— 那条路走通用 PUT /{entity}/selective（S6 已知约束）。
         */
        gatePassNode(name) {
            return {
                name: name,
                variables: [
                    {
                        label: '出门证确认',
                        name: 'gatePassConfirmed',
                        type: this.VALUE_TYPE.SELECT,
                        saveTo: this.SAVE_TO.APPROVE,
                        options: [
                            {value: 1, text: '确认，出门证已办理'}
                        ]
                    },
                    {
                        label: '出门证编号',
                        // S8 的 applyTaskInput 在两个出门证节点读这个变量写 gate_pass_code；
                        // 为空则保留库里已有值
                        name: 'gatePassCode',
                        type: this.VALUE_TYPE.INPUT
                    }
                ],
                withCommentNotNull: true,
                withAttachment: true,
                approveToNextTask: {
                    1: 'task_finance_write_off'
                }
            };
        },

        loadCostCenters() {
            this.$_emApiMixin_get(process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset/costCenters')
                    .then(res => this.costCenters = res || []);
        },

        loadItems() {
            let id = this.$_emWorkflowTaskMixin_businessKey;
            if (!id) {
                return;
            }
            this.itemLoading = true;
            this.$_emApiMixin_get(`${process.env.VUE_APP_EM_ARCHIVE_URL}/emArchFixedAssetDisposal/detail/${id}`)
                    .then(res => {
                        this.items = (res && res.items) || [];
                        this.itemTableKey++;
                    })
                    .finally(() => this.itemLoading = false);
        },
    }
};
</script>

<style scoped>
/*
   b-table 的 sticky-header 包裹层同时带着 MiniTable 的 .table-cover，而后者的
   `max-height: 100% !important` 盖掉了 bootstrap-vue 自带的 300px 上限；父级高度不是
   确定值时这个百分比解析成 none，表格就按内容整高铺开，把下面的内容盖住。
   所以直接给这一层一个确定上限：表格在自己的 sticky 容器里滚动、表头保持吸顶，
   行数少时依然按内容收缩。外层不要再加 max-height —— 那只会裁剪内容或多出一层滚动条。
*/
.fad-items >>> .b-table-sticky-header {
    max-height: 22rem !important;
}
</style>
